import { useState, useEffect } from 'react';
import {
  HubPost as HubPostType,
  MainStore,
  useMainStore,
} from '../stores/mainStore';
import ReactMarkdown from 'react-markdown';
import {
  LinearProgress,
  Typography,
  Box,
  Stack,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import { useLocation, useParams } from 'react-router-dom';
import { Trans } from './Trans';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

interface OptionStats {
  votes: number;
  score: number;
  percentage: number;
}

interface HubPostProps {
  postId?: string;
}

const getTimeLeft = (endTimestamp: number): any => {
  const now = Date.now();
  const timeLeft = endTimestamp - now;

  if (timeLeft <= 0) return <Trans>Voting ended</Trans>;

  const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return <Trans>Less than a minute left</Trans>;
};

export const HubPost = ({ postId }: HubPostProps) => {
  const id = postId || useParams().id;
  const hubLoading = useMainStore((state: MainStore) => state.hubLoading);
  const location = useLocation();
  const isHubPostPage = location.pathname.includes('/app/hubPost/');
  const vote = useMainStore((state: MainStore) => state.vote);
  const userId = useMainStore((state: MainStore) => state.user);
  const loggedIn = useMainStore((state: MainStore) => state.loggedIn);
  const setLoginModalOpen = useMainStore(
    (state: MainStore) => state.setLoginModalOpen
  );
  const [votingId, setVotingId] = useState<string | null>(null);
  const [post, setPost] = useState<HubPostType | undefined>(undefined);
  const getHubPost = useMainStore((state: MainStore) => state.getHubPost);
  const [showCopyTooltip, setShowCopyTooltip] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const notificationId = useMainStore(
    (state: MainStore) => state.notification?.postId
  );
  const clearNotification = useMainStore(
    (state: MainStore) => state.clearNotification
  );
  useEffect(() => {
    if (notificationId && notificationId === id) {
      clearNotification();
    }
  }, [notificationId]);
  useEffect(() => {
    if (hubLoading || !id) return;
    const loadPost = async () => {
      const post = await getHubPost(id);
      setPost(post);
    };
    loadPost();
  }, [hubLoading]);

  if (!post) {
    return (
      <div className="flex justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
      </div>
    );
  }

  const getUserVote = (post: HubPostType) => {
    return post.votes?.find((vote) => vote.userId === userId)?.option;
  };

  const getOptionStats = (post: HubPostType): OptionStats[] => {
    if (!post.options) return [];

    const stats = post.options.map(() => ({
      votes: 0,
      score: 0,
      percentage: 0,
    }));

    if (post.votes && post.votes.length > 0) {
      post.votes.forEach((vote) => {
        if (vote.option >= 0 && vote.option < stats.length) {
          stats[vote.option].votes++;
          stats[vote.option].score += vote.score || 0;
        }
      });

      const totalScore = stats.reduce((sum, stat) => sum + stat.score, 0);

      if (totalScore > 0) {
        stats.forEach((stat) => {
          stat.percentage = (stat.score / totalScore) * 100;
        });
      }
    }

    return stats;
  };

  const handleVote = async (postId: string, optionIndex: number) => {
    if (!loggedIn) {
      setLoginModalOpen(true);
      return;
    }
    try {
      setVotingId(postId);
      const updatedPost = await vote(postId, optionIndex);
      if (updatedPost) {
        setPost(updatedPost);
      }
    } catch (error) {
      console.error('Failed to vote:', error);
    } finally {
      setVotingId(null);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(
      `https://lewdspace.com/app/hubPost/${post._id}`
    );
    setShowCopyTooltip(true);
    setTimeout(() => setShowCopyTooltip(false), 1500);
  };

  const userVote = getUserVote(post);
  const optionStats = getOptionStats(post);
  const totalVotes = post.votes?.length || 0;
  const totalScore = optionStats.reduce((sum, stat) => sum + stat.score, 0);
  const hasEnded = post.endTimestamp ? post.endTimestamp <= Date.now() : false;

  return post ? (
    <div
      className={
        isHubPostPage
          ? 'app-container1 flex flex-col flex-grow overflow-auto pb-8'
          : ''
      }
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-4">
        <div className="mb-2 relative">
          {post.description && (
            <div className="text-gray-600 dark:text-gray-300 mb-4 prose dark:prose-invert max-w-none flex-grow">
              <ReactMarkdown
                components={{
                  h1: ({ children }) => (
                    <Typography
                      sx={{ maxWidth: 'calc(100% - 40px)' }}
                      variant="h4"
                      gutterBottom
                    >
                      {children}
                    </Typography>
                  ),
                  h2: ({ children }) => (
                    <Typography
                      sx={{ maxWidth: 'calc(100% - 40px)' }}
                      variant="h5"
                      gutterBottom
                    >
                      {children}
                    </Typography>
                  ),
                  p: ({ children }) => (
                    <Typography
                      sx={{
                        my: 2,
                      }}
                      variant="body1"
                      gutterBottom
                    >
                      {children}
                    </Typography>
                  ),
                  a: ({ children, href }) => (
                    <a
                      href={href || ''}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:text-blue-700 underline"
                    >
                      {children}
                    </a>
                  ),
                }}
              >
                {isExpanded
                  ? post.descriptionTrans || post.description
                  : (post.descriptionTrans || post.description).length < 300
                    ? post.descriptionTrans || post.description
                    : (post.descriptionTrans || post.description)
                        .slice(0, 300)
                        .split('\n')
                        .slice(0, -1)
                        .join('\n') + '\n ...'}
              </ReactMarkdown>
              {post.description &&
                (post.descriptionTrans || post.description).length > 100 && (
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="text-pink-500 hover:text-pink-600 mt-2 block italic"
                  >
                    {isExpanded ? (
                      <Trans>Show Less</Trans>
                    ) : (
                      <Trans>Show More</Trans>
                    )}
                  </button>
                )}
            </div>
          )}
          <Tooltip
            title={<Trans>Link copied!</Trans>}
            open={showCopyTooltip}
            onClose={() => setShowCopyTooltip(false)}
            arrow
          >
            <IconButton
              onClick={handleCopyLink}
              size="small"
              sx={{
                ml: 1,
                flexShrink: 0,
                color: 'text.secondary',
                '&:hover': { color: 'text.primary' },
                position: 'absolute',
                right: 0,
                top: 0,
              }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
        {post.options && post.options.length > 1 && (
          <Stack spacing={1.5} sx={{ width: '100%', mt: 2 }}>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <Trans>Voting power</Trans>
              </Typography>
              <Stack direction="row" spacing={1}>
                <Chip
                  size="small"
                  label="Free: 1"
                  variant="filled"
                  color="default"
                />
                <Chip
                  size="small"
                  label="Basic: 3"
                  variant="filled"
                  color="primary"
                />
                <Chip
                  size="small"
                  label="VIP: 6"
                  variant="filled"
                  color="secondary"
                />
                <Chip
                  size="small"
                  label="God: 10"
                  variant="filled"
                  color="error"
                />
              </Stack>
            </Box>
            <Box sx={{ position: 'relative' }}>
              {post.options.map((option, index) => {
                const stats = optionStats[index];
                const isSelected = userVote === index;
                const isVoting = votingId === post._id;

                return (
                  <Box
                    key={index}
                    onClick={() =>
                      !isVoting && !hasEnded && handleVote(post._id, index)
                    }
                    sx={{
                      position: 'relative',
                      height: 36,
                      mb: 1,
                      cursor: hasEnded
                        ? 'default'
                        : isVoting
                          ? 'default'
                          : 'pointer',
                      opacity: hasEnded ? 0.8 : 1,
                      '&:hover': {
                        '& .MuiLinearProgress-root': {
                          bgcolor: hasEnded ? '#f5f5f5' : '#e8e8e8',
                        },
                      },
                    }}
                  >
                    <LinearProgress
                      variant="determinate"
                      value={stats.percentage}
                      sx={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        borderRadius: 1,
                        border: '1px solid #d0d0d0',
                        bgcolor: '#f5f5f5',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: isSelected ? '#d0d0d0' : '#e0e0e0',
                          opacity: isVoting ? 0.7 : 1,
                        },
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        height: '100%',
                        px: 2,
                        zIndex: 1,
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'text.primary',
                          fontWeight: isSelected ? 600 : 400,
                        }}
                      >
                        {option}
                        {isVoting && !hasEnded && (
                          <span className="ml-2">
                            <div className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                          </span>
                        )}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.primary',
                          fontWeight: 500,
                        }}
                      >
                        {stats.percentage.toFixed(0)}%
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mt: 2,
                pt: 1,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {totalVotes} <Trans>votes</Trans>
                </Typography>
                {post.endTimestamp && post.endTimestamp > 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontWeight: hasEnded ? 500 : 400,
                      mt: 0.5,
                    }}
                  >
                    {getTimeLeft(post.endTimestamp)}
                  </Typography>
                ) : null}
              </Box>
              <Typography variant="body2" color="text.secondary">
                <Trans>Total score:</Trans> {totalScore}
              </Typography>
            </Box>
          </Stack>
        )}
      </div>
    </div>
  ) : null;
};
